import {z} from "zod/v4-mini"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import {
    isCurveGeometryAttributeSetData,
    isGeometryAttributeSetData,
    isMeshGeometryAttributeSetData,
    isPointGeometryAttributeSetData,
} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeSetData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeSetNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {StringLike} from "#template/nodes/types"
import {stringLike} from "#template/nodes/string/fundamental"
export type GetAttributeSetParameters<TAttributeSet> = {
    name: string
    attributeSetName: StringLike | null
    attributeSet: TAttributeSet | null
}

export const getAttributeSetParametersSchema = <TAttributeSet>(attributeSetSchema: z.ZodMiniType<TAttributeSet, TAttributeSet>) =>
    z.object({
        name: z.string(),
        attributeSetName: z.nullable(stringLike),
        attributeSet: z.nullable(attributeSetSchema),
    })

function getAttributeSetDataFromAttributeSetData<T extends IndexingDim>(
    attributeSetName: string,
    attributeSetData: GeometryAttributeSetData<T>,
): GeometryAttributeSetData<T> | null {
    const value = attributeSetData.data[attributeSetName]
    if (value === undefined) return null

    if (isPointGeometryAttributeSetData(attributeSetData)) {
        const inner = attributeSetData.data[attributeSetName]
        if (isGeometryAttributeSetData(inner) && isPointGeometryAttributeSetData(inner)) {
            return inner
        }
        return null
    } else if (isCurveGeometryAttributeSetData(attributeSetData)) {
        const inner = attributeSetData.data[attributeSetName]
        if (isGeometryAttributeSetData(inner) && isCurveGeometryAttributeSetData(inner)) {
            return inner
        }
        return null
    } else if (isMeshGeometryAttributeSetData(attributeSetData)) {
        const inner = attributeSetData.data[attributeSetName]
        if (isGeometryAttributeSetData(inner) && isMeshGeometryAttributeSetData(inner)) {
            return inner
        }
        return null
    }

    return null
}

export abstract class GeometryGetAttributeSetNode<
    out T extends IndexingDim,
    out TAttributeSet extends EvaluableTemplateNode<GeometryAttributeSetData<T> | null>,
> extends GeometryAttributeSetNode<T, GetAttributeSetParameters<TAttributeSet>> {
    protected override hasValidParameters(
        parameters: GetAttributeSetParameters<TAttributeSet>,
    ): parameters is GetAttributeSetParameters<TAttributeSet> & NonNullableParameters<GetAttributeSetParameters<TAttributeSet>> {
        return parameters.attributeSet !== null && parameters.attributeSetName !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<GetAttributeSetParameters<TAttributeSet>>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeSetData<T> | null>; invalidator?: BuilderInlet<null>} {
        const {attributeSet, attributeSetName} = parameters

        const [evaluatedAttributeSetValid, evaluatedAttributeSetInvalid] = scope.branch(evaluator.evaluate(attributeSet))
        const [evaluatedAttributeSetNameValid, evaluatedAttributeSetNameInvalid] = scope.branch(evaluator.evaluate(attributeSetName))

        const geometryAttributeSetData = scope.pureLambda(
            scope.tuple(evaluatedAttributeSetValid, evaluatedAttributeSetNameValid),
            ([attributeSetData, attributeSetName]) => getAttributeSetDataFromAttributeSetData(attributeSetName, attributeSetData),
            "getAttributeSetDataFromAttributeSetData",
        )

        return {evaluatedGeometryData: geometryAttributeSetData, invalidator: scope.phi(evaluatedAttributeSetInvalid, evaluatedAttributeSetNameInvalid)}
    }
}
